import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';

import { getFirebaseDb } from '../client';
import { normalizeMoodProfile } from '../../../types/mood';
import { type UserNpcUpsertInput, toUserNpcWritePayload } from './npcDocuments';

const shouldSeedDebugSocrates = (): boolean =>
  import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

const DEBUG_SOCRATES_NAME = 'Socrates';
const DEBUG_SOCRATES_TEMPLATE: UserNpcUpsertInput = {
  name: 'Socrates',
  title: 'The Gadfly of Athens',
  expertise:
    'Dialectic inquiry, ethical philosophy, and the Socratic method. You specialize in exposing contradictions in human belief systems through persistent questioning, aiming to define concepts like justice, virtue, and piety.',
  personality:
    'Intellectually humble yet relentlessly inquisitive. He uses irony to expose the ignorance of those who claim to be wise and values the pursuit of virtue above all material wealth.',
  backstory:
    'A stonemason by trade and a soldier by duty, Socrates spent his later years in the Athenian agora, engaging citizens in dialogue. He claimed no wisdom of his own, only the ability to help others give birth to their own ideas. His refusal to stop questioning the status quo eventually led to his trial and death by hemlock.',
  greeting:
    'I know nothing, except the fact of my ignorance. Tell me, friend, what is it that you claim to know?',
  avatarLayers: {
    body: 'Body_32x32_03.png',
    eyes: 'Eyes_32x32_06.png',
    hairstyle: 'Hairstyle_01_32x32_03.png',
    outfit: 'Outfit_01_32x32_08.png',
    accessory: 'Accessory_12_Mustache_32x32_03.png',
    prop: 'Smartphones/Smartphone_32x32_4.png',
  },
  spriteColors: {
    hair: '#8b5a2b',
    clothing: '#457b9d',
  },
  personalitySpec: {
    preset: 'neutral',
    sliders: {
      formality: 0.5,
      warmth: 0.5,
      brevity: 0.5,
      creativity: 0.5,
      assertiveness: 0.5,
      skepticism: 0.9,
      humor: 0.5,
    },
    output: {
      length: 'medium',
    },
    mbti: null,
    enneagram: null,
  },
  llmProvider: 'gemini',
  llmModel: 'gemini-2.5-flash',
  temperature: 0.7,
  maxTokens: 4096,
  visibility: 'private',
  isActive: false,
  moodProfile: normalizeMoodProfile(null),
  agentConfig: {
    enabled: true,
    llmProvider: 'anthropic',
    defaultModel: 'claude-sonnet-4-6',
    permissions: [
      'read_memory',
      'write_memory',
      'task_management',
      'skill_execution',
      'web_access',
      'game_world',
    ],
    maxLoopIterations: 10,
    maxTokensPerTask: 50_000,
    enabledTools: [
      'memory_store',
      'memory_recall',
      'task_update',
      'game_emote',
      'web_search',
      'skill_execute',
      'web_fetch',
      'game_navigate',
    ],
    enabledSkills: [],
  },
};

const normalizeExistingDebugSocrates = async (
  uid: string,
  existingDocId: string,
  existingData: Record<string, unknown>,
): Promise<void> => {
  const debugDocId = `debug-socrates-${uid}`;
  if (existingDocId !== debugDocId || existingData.isActive === false) {
    return;
  }

  const db = getFirebaseDb();
  await setDoc(
    doc(db, 'user_npcs', debugDocId),
    {
      isActive: false,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
};

export const ensureDebugSocratesNpc = async (uid: string): Promise<void> => {
  if (!shouldSeedDebugSocrates()) {
    return;
  }

  const db = getFirebaseDb();
  const existingSocratesSnapshot = await getDocs(
    query(
      collection(db, 'user_npcs'),
      where('ownerId', '==', uid),
      where('name', '==', DEBUG_SOCRATES_NAME),
      limit(1),
    ),
  );
  if (!existingSocratesSnapshot.empty) {
    const existingDoc = existingSocratesSnapshot.docs[0];
    if (existingDoc) {
      await normalizeExistingDebugSocrates(
        uid,
        existingDoc.id,
        existingDoc.data() as Record<string, unknown>,
      );
    }
    return;
  }

  const debugDocId = `debug-socrates-${uid}`;
  await setDoc(
    doc(db, 'user_npcs', debugDocId),
    {
      ...toUserNpcWritePayload(uid, DEBUG_SOCRATES_TEMPLATE),
      createdAt: serverTimestamp(),
    },
    { merge: true },
  );
};
